import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Layout } from "@/components/layout";
import { useAuth } from "@/hooks/useAuth";
import { BillingApiError, fetchCurrentUser, verifyCheckout } from "@/lib/billing-api";
import { PLANS, getCurrentPlanId, unfoldingsLabel } from "@/lib/billing";
import { pollUntilPaid, verifyFailureKind } from "@/lib/entitlement-poll";
import { parsePaidPlan } from "@/lib/upgrade-intent";
import type { AuthUser } from "@/hooks/useAuth";
import type { RawAuthUser } from "@/lib/auth-session";

/** A just-paid session can briefly read "open" while Stripe finishes; retry this long. */
const INCOMPLETE_RETRY_DELAYS_MS = [1_500, 3_000, 5_000];

type State =
  | { kind: "verifying" }
  | { kind: "done" }
  | { kind: "processing" }
  | { kind: "error"; message: string };

/**
 * Stripe Checkout returns here with `?session_id=…&plan=…`. We confirm the session on
 * the server — which runs the same subscription sync as the webhook — and put the
 * refreshed user straight into the auth context, so the new plan shows now rather than
 * whenever the webhook lands. Re-running it (refresh, back button) is harmless.
 *
 * If verification can't confirm it yet (still processing, or a temporary failure), we
 * fall back to re-reading the signed-in user for up to 15s, in case the webhook lands
 * first. That fallback is read-only: it never re-verifies, reconciles or starts checkout.
 *
 * `?plan=` is display-only ("Confirming your upgrade to Plus…"). It is never sent to the
 * server, and the plan shown once confirmed is always the one the server returns.
 */
export default function BillingSuccessPage() {
  const [, setLocation] = useLocation();
  const { user, hydrateUser } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>({ kind: "verifying" });
  const running = useRef<AbortController | null>(null);
  const [{ sessionId, displayPlan }] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { sessionId: params.get("session_id"), displayPlan: parsePaidPlan(params.get("plan")) };
  });
  const displayPlanName = PLANS.find((p) => p.id === displayPlan)?.name ?? null;

  const confirmed = useCallback(
    async (fresh: AuthUser) => {
      hydrateUser(fresh as unknown as RawAuthUser);
      await queryClient.invalidateQueries();
      setState({ kind: "done" });
    },
    [hydrateUser, queryClient],
  );

  const verify = useCallback(async () => {
    if (!sessionId) {
      setState({ kind: "error", message: "This page is missing its checkout reference." });
      return;
    }
    running.current?.abort();
    const controller = new AbortController();
    running.current = controller;
    setState({ kind: "verifying" });

    for (let attempt = 0; ; attempt++) {
      try {
        const { user: fresh } = await verifyCheckout(sessionId);
        if (controller.signal.aborted) return;
        await confirmed(fresh);
        return;
      } catch (err) {
        if (controller.signal.aborted) return;
        const kind = verifyFailureKind(err);
        if (kind === "incomplete" && attempt < INCOMPLETE_RETRY_DELAYS_MS.length) {
          await new Promise((r) => setTimeout(r, INCOMPLETE_RETRY_DELAYS_MS[attempt]));
          if (controller.signal.aborted) return;
          continue;
        }
        if (kind === "permanent") {
          const code = err instanceof BillingApiError ? err.code : null;
          setState({
            kind: "error",
            message:
              code === "unauthenticated"
                ? "Please sign in again to finish confirming your upgrade."
                : "We couldn't match this checkout to your account.",
          });
          return;
        }
        break;
      }
    }

    // Still processing, or a temporary failure: watch for the webhook's result instead.
    const outcome = await pollUntilPaid({ fetchUser: fetchCurrentUser, signal: controller.signal });
    if (outcome.kind === "aborted") return;
    if (outcome.kind === "paid") await confirmed(outcome.user);
    else setState({ kind: "processing" });
  }, [sessionId, confirmed]);

  // Once per mount; leaving the page stops any wait still in progress.
  const verifyRef = useRef(verify);
  verifyRef.current = verify;
  useEffect(() => {
    void verifyRef.current();
    return () => running.current?.abort();
  }, []);

  const plan = PLANS.find((p) => p.id === getCurrentPlanId(user));

  return (
    <Layout hideNav>
      <div className="min-h-screen flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md rounded-[22px] border border-foreground/10 bg-white/70 px-7 py-9 text-center shadow-[0_2px_12px_-10px_hsl(24_20%_20%/0.4)]"
        >
          {state.kind === "verifying" && (
            <>
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
              <h1 className="mt-5 font-display text-2xl font-medium text-foreground">
                {displayPlanName ? `Confirming your upgrade to ${displayPlanName}…` : "Confirming your upgrade…"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">This usually takes a few seconds.</p>
            </>
          )}

          {state.kind === "done" && plan && (
            <>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white">
                <Check className="h-6 w-6" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-medium text-foreground">
                {plan.id === "starter" ? "Your checkout is confirmed" : `Welcome to ${plan.name} ${plan.emoji}`}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {plan.id === "starter"
                  ? "Your plan will update as soon as Stripe finishes processing."
                  : `You now have ${unfoldingsLabel(plan)}. A receipt is on its way to your inbox.`}
              </p>
              <div className="mt-7 flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={() => setLocation("/app")}
                  className="w-full rounded-[13px] bg-primary px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-primary/90"
                >
                  Start unfolding
                </button>
                <button
                  type="button"
                  onClick={() => setLocation("/settings")}
                  className="w-full rounded-[13px] border border-foreground/15 bg-white/60 px-5 py-3 text-sm font-medium text-foreground hover:bg-white"
                >
                  View my plan
                </button>
              </div>
            </>
          )}

          {state.kind === "processing" && (
            <>
              <h1 className="font-display text-2xl font-medium text-foreground">Your upgrade is still processing</h1>
              <p className="mt-2 text-sm text-muted-foreground">You can retry in a moment.</p>
              <p className="mt-2 text-xs text-muted-foreground/80">
                Your plan also updates automatically once Stripe confirms it — you don&apos;t need to pay again.
              </p>
              <div className="mt-6 flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={() => void verify()}
                  className="w-full rounded-[13px] bg-primary px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-primary/90"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => setLocation("/settings")}
                  className="w-full rounded-[13px] border border-foreground/15 bg-white/60 px-5 py-3 text-sm font-medium text-foreground hover:bg-white"
                >
                  Go to my plan
                </button>
              </div>
            </>
          )}

          {state.kind === "error" && (
            <>
              <h1 className="font-display text-2xl font-medium text-foreground">We couldn&apos;t confirm this yet</h1>
              <p className="mt-2 text-sm text-muted-foreground">{state.message}</p>
              <p className="mt-2 text-xs text-muted-foreground/80">
                If you were charged, your plan will still update automatically — you don&apos;t need to pay again.
              </p>
              <div className="mt-6 flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={() => setLocation("/settings")}
                  className="w-full rounded-[13px] border border-foreground/15 bg-white/60 px-5 py-3 text-sm font-medium text-foreground hover:bg-white"
                >
                  Go to my plan
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </Layout>
  );
}
